import { Icon } from '@iconify/react';
import {
  Input as AntdInput,
  Checkbox as AntdCheckbox,
  CheckboxChangeEvent,
} from 'antd';
import type { TextAreaRef } from 'antd/es/input/TextArea';
import {
  RefObject,
  useState,
  useImperativeHandle,
  forwardRef,
  useMemo,
  useEffect,
} from 'react';

import { Popover } from 'components/data-display/Popover';
import { Button } from 'components/general/Button';

import { featureFlags } from 'utils/config/featureFlags';
import {
  AZUMI_SOURCE_OPTIONS,
  SIERRA_SOURCE_OPTIONS,
  SOURCE_OPTIONS,
} from 'utils/maxi-ai/sources';

import { useWorkspaceStore } from 'store/workspace.store';

import type { Source } from 'types/maxi-ai';

export interface ChatInputHandle {
  setInputValue: (value: string) => void;
}

interface ChatInputProps {
  isLoading: boolean;
  onSendMessage: (
    message: string,
    currentWorkspace: string,
    sources: Source[]
  ) => void;
  inputRef: RefObject<TextAreaRef | null>;
}

export const ChatInput = forwardRef<ChatInputHandle, ChatInputProps>(
  ({ isLoading, onSendMessage, inputRef }, ref) => {
    const { workspace } = useWorkspaceStore();

    const dataSources = useMemo(() => {
      if (featureFlags.FF__AZUMI_POC) return AZUMI_SOURCE_OPTIONS;
      if (workspace?.name === 'Sierra') return SIERRA_SOURCE_OPTIONS;
      return SOURCE_OPTIONS;
    }, [workspace]);

    const [inputValue, setInputValue] = useState('');
    const [selectedSources, setSelectedSources] = useState<Source[]>(() =>
      dataSources.map((option) => option.value)
    );
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);

    // Update selectedSources when dataSources changes (e.g., switching workspaces)
    useEffect(() => {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedSources(dataSources.map((option) => option.value));
    }, [dataSources]);

    // Expose input control to parent component
    useImperativeHandle(ref, () => ({
      setInputValue,
    }));

    // Handle source checkbox changes
    const handleSourceCheck = (e: CheckboxChangeEvent) => {
      const { checked, value } = e.target;

      if (checked) {
        setSelectedSources((prev) => prev.concat(value));
      } else {
        if (selectedSources.length <= 1) return;
        setSelectedSources((prev) => prev.filter((id) => id !== value));
      }
    };

    // Send message with current workspace and sources
    const handleSendMessage = () => {
      if (!inputValue.trim() || isLoading || !workspace) return;

      onSendMessage(inputValue, workspace.id, selectedSources);
      setInputValue('');
    };

    const sourceMeta = useMemo(
      () =>
        Object.fromEntries(dataSources.map((o) => [o.value, o])) as Record<
          Source,
          (typeof dataSources)[number]
        >,
      [dataSources]
    );

    return (
      <div className="p-4 flex justify-center items-center">
        <div className="w-full h-32 rounded-2xl border-color-border border-[1px] flex flex-col justify-between py-4 shadow-md">
          {/* Message input */}
          <AntdInput.TextArea
            ref={inputRef}
            value={inputValue}
            variant="borderless"
            onPressEnter={() => !isLoading && handleSendMessage()}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Write your ask here..."
            className="flex-1 resize-none px-4 placeholder:text-color-text-tertiary"
            disabled={isLoading}
            rows={3}
            style={{ resize: 'none' }}
          />

          {/* Controls */}
          <div className="flex justify-between items-center px-4">
            {/* Source selector dropdown */}
            <Popover
              trigger="click"
              content={
                <div className="flex flex-col min-w-48 p-4 shadow-2xl">
                  {dataSources.map((option) => (
                    <AntdCheckbox
                      value={option.value}
                      key={option.value}
                      checked={selectedSources.includes(option.value)}
                      onChange={handleSourceCheck}
                    >
                      <div className="flex items-center gap-2 mt-1">
                        <img
                          alt={option.label}
                          src={option.icon}
                          className="h-5 w-auto"
                        />
                        <div className="text-base">{option.label}</div>
                      </div>
                    </AntdCheckbox>
                  ))}
                </div>
              }
              arrow={false}
              placement="topLeft"
              onOpenChange={setIsPopoverOpen}
            >
              <div className="flex justify-between items-center gap-1 max-w-lg cursor-pointer select-none">
                {selectedSources.length > 0 && (
                  <div className="flex items-center">
                    {selectedSources
                      .slice(0, selectedSources.length > 3 ? 2 : 3)
                      .map((source, i) => (
                        <div
                          className={`border border-color-border rounded-full p-1 flex items-center justify-center bg-white ${i > 0 ? '-ml-2' : ''}`}
                          key={source}
                        >
                          <img
                            alt={sourceMeta[source].label}
                            src={sourceMeta[source].icon}
                            className="w-4 h-4"
                          />
                        </div>
                      ))}
                    {selectedSources.length > 3 && (
                      <div className="text-color-text-secondary text-xs font-medium h-6 w-6 border border-color-border rounded-full flex items-center justify-center -ml-2 bg-white">
                        +{selectedSources.length - 2}
                      </div>
                    )}
                  </div>
                )}
                <div className="text-color-text-secondary text-sm font-medium">
                  Data sources
                </div>
                <Icon
                  icon="carbon:chevron-up"
                  className={`text-base transition-transform duration-500 text-color-text-secondary ${
                    isPopoverOpen ? 'rotate-0' : 'rotate-180'
                  }`}
                />
              </div>
            </Popover>

            {/* Send button */}
            <Button
              className="bg-lime-5"
              icon={<Icon icon="ant-design:send-outlined" />}
              disabled={isLoading || !inputValue.trim()}
              onClick={handleSendMessage}
              size="small"
            />
          </div>
        </div>
      </div>
    );
  }
);

ChatInput.displayName = 'ChatInput';
